import { mergeRefs, useObjectRef } from '@react-aria/utils';
import type { ForwardedRef } from 'react';
import { forwardRef, useContext, useMemo } from 'react';
import type { PopoverProps as AriaPopoverProps } from 'react-aria-components';
import {
  composeRenderProps,
  ModalContext as AriaModalContext,
  Popover as AriaPopover,
} from 'react-aria-components';

import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn } from '@infinex/ui-styles';

import { POPOVER_INSET, PopoverVars } from './constants';
import { DialogTransitionEvents } from './DialogTransitionEvents';
import type { ModalPrimitiveProps } from './ModalPrimitive';

import { SurfaceContext } from '../surface';

export type PopoverPrimitiveProps = Omit<
  AriaPopoverProps,
  | 'arrowBoundaryOffset'
  | 'className'
  | 'containerPadding'
  | 'crossOffset'
  | 'offset'
  | 'style'
  | 'UNSTABLE_portalContainer'
> &
  Pick<ModalPrimitiveProps, 'onOpenComplete' | 'onCloseComplete'> &
  UnsafeStyleProps;

/**
 * Styled popover overlay. Prefer `<Dialog type="popover" />` in most cases.
 */
export const PopoverPrimitive = forwardRef(function PopoverPrimitive(
  props: PopoverPrimitiveProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  const { className, style, ...otherProps } = props;

  // This is a bit of a hack to make the `DialogMarshal` work with popovers.
  const modalContext = useContext(AriaModalContext);
  const mergedRef = useObjectRef(
    useMemo(
      // @ts-expect-error — `ref` is not typed on `ModalContext` but it's available
      () => mergeRefs(forwardedRef, modalContext?.ref),
      // @ts-expect-error — 👆
      [forwardedRef, modalContext?.ref]
    )
  );

  // Popovers receive a "surface" background, so we need to set the correct
  // surface layer for descendant components.
  const surfaceLayer = 1;

  return (
    <AriaPopover
      {...otherProps}
      containerPadding={POPOVER_INSET}
      ref={mergedRef}
      className={cn(
        'bg-surface rounded-base border-secondary border-hairline shadow-lg',
        PopoverVars.tw('max-w-(--Popover-max-width)'),
        // apply animations
        'ease-out-smooth transition-all duration-200',
        'entering:opacity-0 entering:transform-(--origin)',
        'exiting:opacity-0 exiting:transform-(--origin)',
        // placement config
        '[--placement-offset:8px]',
        'placement-bottom:[--origin:translateY(calc(var(--placement-offset)*-1))]',
        'placement-top:[--origin:translateY(var(--placement-offset))]',
        'placement-left:[--origin:translateX(var(--placement-offset))]',
        'placement-right:[--origin:translateX(calc(var(--placement-offset)*-1))]',
        className
      )}
      style={{
        ...PopoverVars.apply((vars) => ({
          inset: POPOVER_INSET,
          maxHeight: '50vh',
          maxWidth: `calc(100vw - ${vars.inset} * 2)`,
          padding: 16,
        })),
        ...style,
      }}
    >
      {composeRenderProps(props.children, (children, renderProps) => (
        <DialogTransitionEvents
          onCloseComplete={props.onCloseComplete}
          onOpenComplete={props.onOpenComplete}
          {...renderProps}
        >
          <SurfaceContext.Provider value={surfaceLayer}>
            {children}
          </SurfaceContext.Provider>
        </DialogTransitionEvents>
      ))}
    </AriaPopover>
  );
});
